import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import {
  NotificationService,
  buildNotificationSyncKey,
} from '../services/NotificationService';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useGoals } from './GoalsContext';
import type { Goal } from './GoalsContext';
import * as XLSX from 'xlsx';
import { Platform, Alert } from 'react-native';
import { getSafeRNBlobUtil, getSafeRNHTMLtoPDF } from '../utils/platformUtils';
import SessionManager from '../services/SessionManager';
import { auth } from '../config/firebase';
import { normalizeSecuritySettings } from '../utils/securitySettings';

export interface UserProfile {
  displayName: string;
  email: string;
  phoneNumber: string;
  avatar?: string;
}

export interface SettingsState {
  profile: UserProfile;
  currency: {
    code: string;
    symbol: string;
  };
  dateFormat: string;
  income: {
    frequency: 'monthly' | 'bi-weekly' | 'weekly' | 'daily';
    customDays?: number[];
    reminders: boolean;
  };
  notifications: {
    goalProgress: boolean;
    deadlineNearing: boolean;
    paymentInput: boolean;
    preferEmail: boolean;
  };
  security: {
    biometric: boolean;
    appLock: boolean;
    pinCode?: string;
  };
  notificationsEnabled: boolean;
}

export interface SettingsContextType {
  settings: SettingsState;
  updateProfile: (profile: Partial<UserProfile>) => Promise<void>;
  updateCurrency: (code: string, symbol: string) => Promise<void>;
  updateDateFormat: (format: string) => Promise<void>;
  updateIncomeSettings: (income: Partial<SettingsState['income']>) => Promise<void>;
  updateNotifications: (notifications: Partial<SettingsState['notifications']>) => Promise<void>;
  updateSecurity: (security: Partial<SettingsState['security']>) => Promise<void>;
  clearPin: () => Promise<void>;
  exportData: (format: 'csv' | 'xlsx' | 'pdf') => Promise<string>;
  resetSettings: () => Promise<void>;
}

const defaultSettings: SettingsState = {
  profile: {
    displayName: '',
    email: '',
    phoneNumber: '',
  },
  currency: {
    code: 'USD',
    symbol: '$',
  },
  dateFormat: 'MM/DD/YYYY',
  income: {
    frequency: 'monthly',
    reminders: true,
  },
  notifications: {
    goalProgress: true,
    deadlineNearing: true,
    paymentInput: true,
    preferEmail: false,
  },
  security: {
    biometric: false,
    appLock: false,
  },
  notificationsEnabled: true,
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider = ({ children }: SettingsProviderProps) => {
  const [settings, setSettings] = useState<SettingsState>(defaultSettings);
  const { goals } = useGoals();
  const [notificationToken, setNotificationToken] = useState<string | undefined>();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const notificationSyncKeyRef = useRef('');
  const sessionManager = SessionManager.getInstance();

  // Initialize notifications
  useEffect(() => {
    const initializeNotifications = async () => {
      const token = await NotificationService.registerForPushNotificationsAsync();
      setNotificationToken(token || undefined);
    };

    initializeNotifications();
  }, []);

  // Listen to auth state changes and reset settings when user changes
  useEffect(() => {
    const authInstance = typeof auth === 'function' ? auth() : auth;
    const unsubscribe = authInstance.onAuthStateChanged(async (user: any) => {
      if (!user) {
        // User logged out - reset to default settings
        console.log('SettingsContext: User logged out, resetting to defaults');
        setSettings(defaultSettings);
        setCurrentUserId(null);
      } else if (user.uid !== currentUserId) {
        // Different user logged in - load their settings
        console.log('SettingsContext: New user logged in, loading their settings', user.uid);
        setCurrentUserId(user.uid);
        
        try {
          const userSettingsKey = `app_settings_${user.uid}`;
          const storedSettings = await AsyncStorage.getItem(userSettingsKey);
          if (storedSettings) {
            const parsedSettings = JSON.parse(storedSettings);
            const loadedSecurity = normalizeSecuritySettings({
              ...defaultSettings.security,
              ...parsedSettings.security,
            });
            const mergedSettings = {
              ...defaultSettings,
              ...parsedSettings,
              security: loadedSecurity,
              profile: {
                ...defaultSettings.profile,
                ...parsedSettings.profile,
                displayName: user.displayName || parsedSettings.profile?.displayName || '',
                email: user.email || parsedSettings.profile?.email || '',
              },
            };
            setSettings(mergedSettings);
          } else {
            // New user - initialize with defaults and user info
            setSettings({
              ...defaultSettings,
              profile: {
                ...defaultSettings.profile,
                displayName: user.displayName || '',
                email: user.email || '',
              }
            });
          }
        } catch (error) {
          console.error('Failed to load user settings:', error);
          setSettings({
            ...defaultSettings,
            profile: {
              ...defaultSettings.profile,
              displayName: user.displayName || '',
              email: user.email || '',
            }
          });
        }
      }
    });

    return () => unsubscribe();
  }, [currentUserId]);

  // Sync app lock settings with session manager
  useEffect(() => {
    sessionManager.updateSessionConfig({
      isAppLockEnabled: settings.security.appLock,
      isBiometricEnabled: settings.security.biometric,
      timeoutDuration: 5 * 60 * 1000, // 5 minutes
    });
  }, [settings.security.appLock, settings.security.biometric, sessionManager]);

  // Sync scheduled notifications when settings or goal schedule data change (not on every reload)
  useEffect(() => {
    if (Platform.OS === 'web') return;

    const syncNotifications = async () => {
      if (!settings.notificationsEnabled) {
        notificationSyncKeyRef.current = '';
        await NotificationService.cancelAllNotifications();
        return;
      }

      const syncKey = buildNotificationSyncKey(settings.notifications, goals);
      if (syncKey === notificationSyncKeyRef.current) {
        return;
      }
      notificationSyncKeyRef.current = syncKey;

      await NotificationService.syncGoalNotifications(goals, settings.notifications);
    };

    syncNotifications();
  }, [settings.notifications, settings.notificationsEnabled, goals]);

  // Save settings to user-specific AsyncStorage key
  const saveSettings = async (newSettings: SettingsState) => {
    try {
      if (currentUserId) {
        const userSettingsKey = `app_settings_${currentUserId}`;
        await AsyncStorage.setItem(userSettingsKey, JSON.stringify(newSettings));
      } else {
        // Fallback to general key if no user (shouldn't happen)
        await AsyncStorage.setItem('app_settings', JSON.stringify(newSettings));
      }
      setSettings(newSettings);
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  const updateProfile = async (profile: Partial<UserProfile>) => {
    const newSettings = {
      ...settings,
      profile: { ...settings.profile, ...profile },
    };
    await saveSettings(newSettings);
  };

  const updateCurrency = async (code: string, symbol: string) => {
    const newSettings = {
      ...settings,
      currency: { code, symbol },
    };
    await saveSettings(newSettings);
  };

  const updateDateFormat = async (format: string) => {
    const newSettings = {
      ...settings,
      dateFormat: format,
    };
    await saveSettings(newSettings);
  };

  const updateIncomeSettings = async (income: Partial<SettingsState['income']>) => {
    const newSettings = {
      ...settings,
      income: { ...settings.income, ...income },
    };
    await saveSettings(newSettings);
  };

  const updateNotifications = async (notifications: Partial<SettingsState['notifications']>) => {
    const newSettings = {
      ...settings,
      notifications: { ...settings.notifications, ...notifications },
    };
    await saveSettings(newSettings);
  };

  const updateSecurity = async (security: Partial<SettingsState['security']>) => {
    const newSettings = {
      ...settings,
      security: normalizeSecuritySettings(settings.security, security),
    };
    await saveSettings(newSettings);
  };

  const clearPin = async () => {
    const newSettings = {
      ...settings,
      security: {
        ...settings.security,
        pinCode: undefined,
        appLock: false, // Also disable app lock for safety
      },
    };
    await saveSettings(newSettings);
  };

  const handleNotificationSettingsChange = async (value: boolean) => {
    try {
      if (value) {
        const token = await NotificationService.registerForPushNotificationsAsync();
        if (!token && Platform.OS !== 'web') {
          Alert.alert(
            'Permission Required',
            'Please enable notifications in your device settings to receive goal updates.'
          );
          return;
        }
      } else if (Platform.OS !== 'web') {
        await NotificationService.cancelAllNotifications();
      }

      await saveSettings({ ...settings, notificationsEnabled: value });

      if (value) {
        notificationSyncKeyRef.current = '';
        await NotificationService.syncGoalNotifications(goals, settings.notifications);
      }
    } catch (error) {
      console.error('Error updating notification settings:', error);
      Alert.alert(
        'Error',
        'Failed to update notification settings. Please try again.'
      );
    }
  };

  const convertToCSV = (data: { goals: Goal[] }) => {
    // Format value for CSV to handle commas and special characters
    const formatCSVValue = (value: string | number | undefined | null) => {
      if (value === undefined || value === null) return '';
      const str = value.toString();
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // CSV headers
    const headers = [
      'Goal ID',
      'Goal Name',
      'Type',
      'Target Amount',
      'Current Amount',
      'Progress %',
      'Monthly Contribution',
      'Priority',
      'Allocated %',
      'Deadline',
      'Created At',
      'Last Updated',
      'Status',
      'APR %',
      'Original Amount',
      'Total Interest',
      'Interest Paid',
      'Payment ID',
      'Payment Amount',
      'Payment Date',
      'Payment Note'
    ];

    // Each row is a goal, or a goal+payment if payments exist
    const rows: string[] = [];
    data.goals.forEach(goal => {
      const progressPercent = ((goal.currentAmount / goal.targetAmount) * 100).toFixed(2);
      const isDebt = goal.type === 'Debt';
      const originalAmount = isDebt ? goal.originalTargetAmount || goal.targetAmount : '';
      const totalInterest = isDebt && goal.originalTargetAmount ? goal.targetAmount - goal.originalTargetAmount : '';
      let interestPaid = '';
      if (isDebt && goal.apr && goal.originalTargetAmount && goal.payments?.length > 0) {
        const dailyRate = goal.apr / 100 / 365;
        let runningBalance = goal.originalTargetAmount;
        let totalInterestPaid = 0;
        let lastPaymentDate: Date | null = null;
        const sortedPayments = [...goal.payments].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        sortedPayments.forEach(payment => {
          const paymentDate = new Date(payment.date);
          if (lastPaymentDate) {
            const daysDiff = Math.floor((paymentDate.getTime() - lastPaymentDate.getTime()) / (1000 * 60 * 60 * 24));
            const interestAccrued = runningBalance * dailyRate * daysDiff;
            totalInterestPaid += interestAccrued;
          }
          runningBalance = Math.max(0, runningBalance - payment.amount);
          lastPaymentDate = paymentDate;
        });
        interestPaid = totalInterestPaid.toString();
      }
      // If no payments, output a single row for the goal
      if (!goal.payments || goal.payments.length === 0) {
        rows.push([
          formatCSVValue(goal.id),
          formatCSVValue(goal.name),
          formatCSVValue(goal.type),
          formatCSVValue(goal.targetAmount),
          formatCSVValue(goal.currentAmount),
          formatCSVValue(progressPercent + '%'),
          formatCSVValue(goal.monthlyContribution),
          formatCSVValue(goal.priority),
          formatCSVValue(goal.allocatedPercentage.toFixed(2) + '%'),
          formatCSVValue(goal.deadline),
          formatCSVValue(goal.createdAt),
          formatCSVValue(goal.lastUpdated),
          formatCSVValue(goal.completedDate ? 'Completed' : 'In Progress'),
          formatCSVValue(isDebt ? goal.apr?.toString() || '' : ''),
          originalAmount ? formatCSVValue(originalAmount) : '',
          totalInterest ? formatCSVValue(totalInterest) : '',
          interestPaid ? formatCSVValue(parseFloat(interestPaid)) : '',
          '', '', '', '' // No payment info
        ].join(','));
      } else {
        // Output a row for each payment
        goal.payments.forEach(payment => {
          rows.push([
            formatCSVValue(goal.id),
            formatCSVValue(goal.name),
            formatCSVValue(goal.type),
            formatCSVValue(goal.targetAmount),
            formatCSVValue(goal.currentAmount),
            formatCSVValue(progressPercent + '%'),
            formatCSVValue(goal.monthlyContribution),
            formatCSVValue(goal.priority),
            formatCSVValue(goal.allocatedPercentage.toFixed(2) + '%'),
            formatCSVValue(goal.deadline),
            formatCSVValue(goal.createdAt),
            formatCSVValue(goal.lastUpdated),
            formatCSVValue(goal.completedDate ? 'Completed' : 'In Progress'),
            formatCSVValue(isDebt ? goal.apr?.toString() || '' : ''),
            originalAmount ? formatCSVValue(originalAmount) : '',
            totalInterest ? formatCSVValue(totalInterest) : '',
            interestPaid ? formatCSVValue(parseFloat(interestPaid)) : '',
            formatCSVValue(payment.id),
            formatCSVValue(payment.amount),
            formatCSVValue(payment.date),
            formatCSVValue(payment.note || '')
          ].join(','));
        });
      }
    });
    
    // For web platform, don't add BOM as it can cause issues
    if (Platform.OS === 'web') {
      return [headers.join(','), ...rows].join('\n');
    } else {
      // Add BOM for Excel compatibility on native platforms
      return '\uFEFF' + [headers.join(','), ...rows].join('\n');
    }
  };

  const convertToExcel = (data: { goals: Goal[] }) => {
    // Format currency using the settings
    const formatCurrency = (amount: number) => {
      const roundedAmount = Math.round(amount * 100) / 100;
      return `${settings.currency.symbol}${roundedAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    };

    // For web platform, use a simpler approach to avoid crypto issues
    if (Platform.OS === 'web') {
      // Create a simple CSV-like format that can be opened in Excel
      const headers = [
        'Goal ID',
        'Name',
        'Type',
        'Target Amount',
        'Current Amount',
        'Progress %',
        'Monthly Contribution',
        'Priority',
        'Allocated %',
        'Deadline',
        'Created At',
        'Last Updated',
        'Status',
        'APR %',
        'Original Amount',
        'Total Interest',
        'Interest Paid'
      ];

      const rows: string[] = [];
      data.goals.forEach(goal => {
        const progress = ((goal.currentAmount / goal.targetAmount) * 100).toFixed(2);
        const isDebt = goal.type === 'Debt';
        const originalAmount = isDebt ? goal.originalTargetAmount || goal.targetAmount : '';
        const totalInterest = isDebt && goal.originalTargetAmount ? goal.targetAmount - goal.originalTargetAmount : '';
        let interestPaid = '';
        if (isDebt && goal.apr && goal.originalTargetAmount && goal.payments?.length > 0) {
          const dailyRate = goal.apr / 100 / 365;
          let runningBalance = goal.originalTargetAmount;
          let totalInterestPaid = 0;
          let lastPaymentDate: Date | null = null;
          const sortedPayments = [...goal.payments].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
          sortedPayments.forEach(payment => {
            const paymentDate = new Date(payment.date);
            if (lastPaymentDate) {
              const daysDiff = Math.floor((paymentDate.getTime() - lastPaymentDate.getTime()) / (1000 * 60 * 60 * 24));
              const interestAccrued = runningBalance * dailyRate * daysDiff;
              totalInterestPaid += interestAccrued;
            }
            runningBalance = Math.max(0, runningBalance - payment.amount);
            lastPaymentDate = paymentDate;
          });
          interestPaid = totalInterestPaid.toString();
        }

        rows.push([
          goal.id,
          goal.name,
          goal.type,
          formatCurrency(goal.targetAmount),
          formatCurrency(goal.currentAmount),
          progress + '%',
          formatCurrency(goal.monthlyContribution),
          goal.priority,
          goal.allocatedPercentage.toFixed(2) + '%',
          goal.deadline,
          goal.createdAt,
          goal.lastUpdated,
          goal.completedDate ? 'Completed' : 'In Progress',
          isDebt ? (goal.apr?.toString() || '') : '',
          originalAmount ? formatCurrency(originalAmount) : '',
          totalInterest ? formatCurrency(totalInterest) : '',
          interestPaid ? formatCurrency(parseFloat(interestPaid)) : ''
        ].join('\t')); // Use tab separator for Excel compatibility
      });

      // Return tab-separated data that Excel can open
      return [headers.join('\t'), ...rows].join('\n');
    }

    // For native platforms, use the full XLSX library
    // Create workbook
    const wb = XLSX.utils.book_new();

    // Create Summary worksheet
    const savingsGoals = data.goals.filter(goal => goal.type === 'Savings');
    const debtGoals = data.goals.filter(goal => goal.type === 'Debt');
    const investmentGoals = data.goals.filter(goal => goal.type === 'Investment');

    const calculateTypeStats = (goals: Goal[]) => {
      if (goals.length === 0) return { total: 0, target: 0, progress: 0 };
      const total = goals.reduce((sum, goal) => sum + goal.currentAmount, 0);
      const target = goals.reduce((sum, goal) => sum + goal.targetAmount, 0);
      const progress = target > 0 ? (total / target) * 100 : 0;
      return { total, target, progress };
    };

    const savingsStats = calculateTypeStats(savingsGoals);
    const debtStats = calculateTypeStats(debtGoals);
    const investmentStats = calculateTypeStats(investmentGoals);

    const summaryData = [
      {
        'Category': 'Total Savings',
        'Total Amount': formatCurrency(savingsStats.total),
        'Target Amount': formatCurrency(savingsStats.target),
        'Progress': `${savingsStats.progress.toFixed(2)}%`
      },
      {
        'Category': 'Total Debt Paid',
        'Total Amount': formatCurrency(debtStats.total),
        'Target Amount': formatCurrency(debtStats.target),
        'Progress': `${debtStats.progress.toFixed(2)}%`
      },
      {
        'Category': 'Total Investment',
        'Total Amount': formatCurrency(investmentStats.total),
        'Target Amount': formatCurrency(investmentStats.target),
        'Progress': `${investmentStats.progress.toFixed(2)}%`
      }
    ];

    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Create Goals worksheet with additional debt fields
    const goalsData = data.goals.map(goal => {
      const progressPercent = ((goal.currentAmount / goal.targetAmount) * 100).toFixed(2);
      const isDebt = goal.type === 'Debt';
      const originalAmount = isDebt ? goal.originalTargetAmount || goal.targetAmount : null;
      const totalInterest = isDebt && goal.originalTargetAmount ? goal.targetAmount - goal.originalTargetAmount : null;
      
      // Calculate interest paid for debt goals
      let interestPaid = null;
      if (isDebt && goal.apr && goal.originalTargetAmount && goal.payments?.length > 0) {
        const dailyRate = goal.apr / 100 / 365;
        let runningBalance = goal.originalTargetAmount;
        let totalInterestPaid = 0;
        let lastPaymentDate: Date | null = null;

        const sortedPayments = [...goal.payments].sort((a, b) => 
          new Date(a.date).getTime() - new Date(b.date).getTime()
        );

        sortedPayments.forEach(payment => {
          const paymentDate = new Date(payment.date);
          if (lastPaymentDate) {
            const daysDiff = Math.floor(
              (paymentDate.getTime() - lastPaymentDate.getTime()) / (1000 * 60 * 60 * 24)
            );
            const interestAccrued = runningBalance * dailyRate * daysDiff;
            totalInterestPaid += interestAccrued;
          }
          runningBalance = Math.max(0, runningBalance - payment.amount);
          lastPaymentDate = paymentDate;
        });
        interestPaid = totalInterestPaid;
      }

      return {
        'Goal ID': goal.id,
        'Name': goal.name,
        'Type': goal.type,
        'Target Amount': formatCurrency(goal.targetAmount),
        'Current Amount': formatCurrency(goal.currentAmount),
        'Progress %': progressPercent + '%',
        'Monthly Contribution': formatCurrency(goal.monthlyContribution),
        'Priority': goal.priority,
        'Allocated %': goal.allocatedPercentage.toFixed(2) + '%',
        'Deadline': goal.deadline,
        'Created At': goal.createdAt,
        'Last Updated': goal.lastUpdated,
        'Status': goal.completedDate ? 'Completed' : 'In Progress',
        'APR %': isDebt ? goal.apr || null : null,
        'Original Amount': originalAmount ? formatCurrency(originalAmount) : null,
        'Total Interest': totalInterest ? formatCurrency(totalInterest) : null,
        'Interest Paid': interestPaid ? formatCurrency(interestPaid) : null,
        'Payment Sheet': goal.payments.length > 0 ? `Payments_${data.goals.indexOf(goal) + 1}` : 'No payments'
      };
    });

    const wsGoals = XLSX.utils.json_to_sheet(goalsData);
    XLSX.utils.book_append_sheet(wb, wsGoals, 'Goals');

    // Create Payment History worksheets for each goal
    data.goals.forEach((goal, index) => {
      const paymentData = goal.payments.map(payment => ({
        'Goal Name': goal.name,
        'Payment ID': payment.id,
        'Amount': formatCurrency(payment.amount),
        'Date': payment.date,
        'Note': payment.note || '',
      }));

      if (paymentData.length > 0) {
        const wsPayments = XLSX.utils.json_to_sheet(paymentData);
        // Truncate sheet name to ensure it doesn't exceed 31 characters
        const sheetName = `Payments_${index + 1}`;
        XLSX.utils.book_append_sheet(wb, wsPayments, sheetName);
      }
    });

    // Generate Excel file
    const wbout = XLSX.write(wb, { type: 'base64', bookType: 'xlsx' });
    return wbout;
  };

  const convertToPDF = async (data: { goals: Goal[] }) => {

    // Format currency using the settings
    const formatCurrency = (amount: number) => {
      const roundedAmount = Math.round(amount * 100) / 100;
      return `${settings.currency.symbol}${roundedAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    };

    // Create HTML content for PDF (shared for both web and native)
    // --- HTML TABLE GENERATION (copied from native block) ---
    const brandGreen = '#2E7D32';
    const totalGoals = data.goals.length;
    const completedGoals = data.goals.filter(g => g.completedDate).length;
    const totalTarget = data.goals.reduce((sum, g) => sum + g.targetAmount, 0);
    const totalCurrent = data.goals.reduce((sum, g) => sum + g.currentAmount, 0);
    const overallProgress = totalTarget > 0 ? (totalCurrent / totalTarget) * 100 : 0;

    let goalsHtml = '';
    data.goals.forEach((goal, idx) => {
      const progress = ((goal.currentAmount / goal.targetAmount) * 100);
      const progressText = progress.toFixed(2);
      const progressColor = progress >= 75 ? brandGreen : progress >= 50 ? '#FFA000' : '#D32F2F';
      const status = goal.completedDate ? 'Completed' : 'In Progress';
      const rowBg = idx % 2 === 0 ? '#ffffff' : '#f5f5f5';
      goalsHtml += `
        <tr style="background-color: ${rowBg};">
          <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; font-weight: 500;">${goal.name}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; text-align: center;">${goal.type}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; text-align: right;">${formatCurrency(goal.targetAmount)}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; text-align: right;">${formatCurrency(goal.currentAmount)}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; text-align: center; color: ${progressColor}; font-weight: bold;">${progressText}%</td>
          <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; text-align: center; color: ${goal.completedDate ? brandGreen : '#666'}; font-weight: ${goal.completedDate ? 'bold' : 'normal'};">${status}</td>
        </tr>
      `;
    });

    // --- SINGLE COLUMN SUMMARY FOR PDF RENDERING ---
    const summaryTable = `
      <table style="width: 350px; border-collapse: collapse; margin-bottom: 20px; font-size: 13px;">
        <tr><td style="padding: 6px 12px; font-weight: bold; color: ${brandGreen}; width: 140px;">Total Goals:</td><td style="padding: 6px 12px;">${totalGoals}</td></tr>
        <tr><td style="padding: 6px 12px; font-weight: bold; color: ${brandGreen};">Completed:</td><td style="padding: 6px 12px;">${completedGoals}</td></tr>
        <tr><td style="padding: 6px 12px; font-weight: bold; color: ${brandGreen};">Total Target:</td><td style="padding: 6px 12px;">${formatCurrency(totalTarget)}</td></tr>
        <tr><td style="padding: 6px 12px; font-weight: bold; color: ${brandGreen};">Total Current:</td><td style="padding: 6px 12px;">${formatCurrency(totalCurrent)}</td></tr>
        <tr><td style="padding: 6px 12px; font-weight: bold; color: ${brandGreen};">Overall Progress:</td><td style="padding: 6px 12px;">${overallProgress.toFixed(2)}%</td></tr>
      </table>
    `;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>GoalMint Goals Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.4; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid ${brandGreen}; padding-bottom: 20px; }
          .goals-table { width: 100%; border-collapse: collapse; margin-top: 15px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); font-size: 13px; }
          .goals-table th, .goals-table td { word-break: break-word; max-width: 180px; }
          .table-header { background-color: ${brandGreen}; color: white; }
          .table-header th { padding: 10px; text-align: left; font-weight: bold; font-size: 13px; }
          .goals-section { margin-top: 20px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 style="color: ${brandGreen}; margin: 0; font-size: 28px;">GoalMint Goals Report</h1>
          <p style="margin: 8px 0 0 0; color: #666; font-size: 16px;">Generated: ${new Date().toLocaleDateString()}</p>
        </div>
        <h2 style="color: ${brandGreen}; font-size: 20px; margin: 20px 0 10px 0;">Summary</h2>
        ${summaryTable}
        <div class="goals-section">
          <h2 style="color: ${brandGreen}; font-size: 20px; margin-bottom: 15px;">Your Goals</h2>
          <table class="goals-table">
            <thead class="table-header">
              <tr>
                <th style="text-align: left; min-width: 120px; max-width: 180px;">Goal Name</th>
                <th style="text-align: center; min-width: 70px;">Type</th>
                <th style="text-align: right; min-width: 100px;">Target Amount</th>
                <th style="text-align: right; min-width: 100px;">Current Amount</th>
                <th style="text-align: center; min-width: 80px;">Progress</th>
                <th style="text-align: center; min-width: 70px;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${goalsHtml}
            </tbody>
          </table>
        </div>
      </body>
      </html>
    `;

    if (Platform.OS === 'web') {
      // For web platform, use jsPDF with html(), and return sentinel value to prevent double download
      try {
        const jsPDF = require('jspdf').jsPDF;
        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        await doc.html(htmlContent, {
          callback: function (doc: any) {
            doc.save('goalmint_report.pdf');
          },
          margin: [20, 20, 20, 20],
          autoPaging: 'text',
          x: 0,
          y: 0
        });
        return '__PDF_HANDLED__';
      } catch (error) {
        console.error('Web PDF generation failed:', error);
        throw new Error('PDF generation not supported in web mode');
      }
    }

    // --- NATIVE PDF EXPORT ---
    try {
      const RNHTMLtoPDF = await getSafeRNHTMLtoPDF();
      
      if (!RNHTMLtoPDF) {
        throw new Error('PDF generation not available');
      }

      // Create simple, clean HTML content
      const brandGreen = '#2E7D32';
      
      // Summary data
      const totalGoals = data.goals.length;
      const completedGoals = data.goals.filter(g => g.completedDate).length;
      const totalTarget = data.goals.reduce((sum, g) => sum + g.targetAmount, 0);
      const totalCurrent = data.goals.reduce((sum, g) => sum + g.currentAmount, 0);
      const overallProgress = totalTarget > 0 ? (totalCurrent / totalTarget) * 100 : 0;

      let goalsHtml = '';
      data.goals.forEach((goal, idx) => {
        const progress = ((goal.currentAmount / goal.targetAmount) * 100);
        const progressText = progress.toFixed(2);
        const progressColor = progress >= 75 ? brandGreen : progress >= 50 ? '#FFA000' : '#D32F2F';
        const status = goal.completedDate ? 'Completed' : 'In Progress';
        const rowBg = idx % 2 === 0 ? '#ffffff' : '#f5f5f5';
        
        goalsHtml += `
          <tr style="background-color: ${rowBg};">
            <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; font-weight: 500;">${goal.name}</td>
            <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; text-align: center;">${goal.type}</td>
            <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; text-align: right;">${formatCurrency(goal.targetAmount)}</td>
            <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; text-align: right;">${formatCurrency(goal.currentAmount)}</td>
            <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; text-align: center; color: ${progressColor}; font-weight: bold;">${progressText}%</td>
            <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; text-align: center; color: ${goal.completedDate ? brandGreen : '#666'}; font-weight: ${goal.completedDate ? 'bold' : 'normal'};">${status}</td>
          </tr>
        `;
      });

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>GoalMint Goals Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              color: #333;
              line-height: 1.4;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              border-bottom: 3px solid ${brandGreen};
              padding-bottom: 20px;
            }
            .summary { 
              background: #E8F5E9; 
              padding: 20px; 
              border-radius: 8px; 
              margin-bottom: 30px;
              border-left: 5px solid ${brandGreen};
            }
            .goals-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 15px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            .table-header {
              background-color: ${brandGreen};
              color: white;
            }
            .table-header th {
              padding: 15px 12px;
              text-align: left;
              font-weight: bold;
              font-size: 14px;
            }
            .goals-section {
              margin-top: 20px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1 style="color: ${brandGreen}; margin: 0; font-size: 28px;">GoalMint Goals Report</h1>
            <p style="margin: 8px 0 0 0; color: #666; font-size: 16px;">Generated: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="summary">
            <h2 style="color: ${brandGreen}; margin: 0 0 15px 0; font-size: 20px;">Summary</h2>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
              <div>
                <p style="margin: 5px 0;"><strong>Total Goals:</strong> ${totalGoals}</p>
                <p style="margin: 5px 0;"><strong>Completed:</strong> ${completedGoals}</p>
                <p style="margin: 5px 0;"><strong>Overall Progress:</strong> ${overallProgress.toFixed(2)}%</p>
              </div>
              <div>
                <p style="margin: 5px 0;"><strong>Total Target:</strong> ${formatCurrency(totalTarget)}</p>
                <p style="margin: 5px 0;"><strong>Total Current:</strong> ${formatCurrency(totalCurrent)}</p>
              </div>
            </div>
          </div>

          <div class="goals-section">
            <h2 style="color: ${brandGreen}; font-size: 20px; margin-bottom: 15px;">Your Goals</h2>
            <table class="goals-table">
              <thead class="table-header">
                <tr>
                  <th style="text-align: left;">Goal Name</th>
                  <th style="text-align: center;">Type</th>
                  <th style="text-align: right;">Target Amount</th>
                  <th style="text-align: right;">Current Amount</th>
                  <th style="text-align: center;">Progress</th>
                  <th style="text-align: center;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${goalsHtml}
              </tbody>
            </table>
          </div>
        </body>
        </html>
      `;

      const options = {
        html: htmlContent,
        fileName: 'goalmint_report',
        directory: Platform.OS === 'ios' ? 'Documents' : 'Downloads',
        base64: false,
        height: 792,
        width: 612,
      };

      const pdf = await RNHTMLtoPDF.convert(options);
      
      if (pdf && pdf.filePath) {
        if (Platform.OS === 'android') {
          // Android: Add to downloads and show notification
          const RNBlobUtil = await getSafeRNBlobUtil();
          if (RNBlobUtil) {
            await RNBlobUtil.android.addCompleteDownload({
              title: 'goalmint_report.pdf',
              description: 'GoalMint Goals Report',
              mime: 'application/pdf',
              path: pdf.filePath,
              showNotification: true,
            });
            
            try {
              await RNBlobUtil.android.actionViewIntent(pdf.filePath, 'application/pdf');
            } catch (viewError) {
              // PDF was created successfully, just couldn't open it
              console.log('PDF created but could not open:', viewError);
            }
          }
        }
        
        // Return the file path for iOS (SettingsScreen will handle sharing)
        return pdf.filePath;
      } else {
        throw new Error('PDF creation failed');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      throw error;
    }
  };

  const exportData = async (format: 'csv' | 'xlsx' | 'pdf') => {
    try {
      const exportData = {
        goals
      };

      let result: string;
      switch (format) {
        case 'csv':
          result = convertToCSV(exportData);
          return result;
        case 'xlsx':
          result = convertToExcel(exportData);
          return result;
        case 'pdf':
          result = await convertToPDF(exportData);
          return result || 'PDF export completed';
        default:
          throw new Error('Unsupported export format');
      }
    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  };

  // Ensures appLock is always false for new users, regardless of any accidental override
  const resetSettings = async () => {
    const safeDefaults = {
      ...defaultSettings,
      security: {
        ...defaultSettings.security,
        appLock: false,
        biometric: false,
        pinCode: undefined,
      },
    };
    await saveSettings(safeDefaults);
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        updateProfile,
        updateCurrency,
        updateDateFormat,
        updateIncomeSettings,
        updateNotifications,
        updateSecurity,
        clearPin,
        exportData,
        resetSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsProvider;